/* eslint-disable consistent-return */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Virtualizer } from '@tanstack/react-virtual';

import * as emitterEvents from 'constants/emitterEvents';
import Emitter from 'services/eventEmitter';

type UseMessageScrollProps = {
  rowVirtualizer: Virtualizer<HTMLUListElement, Element>;
  itemsLength: number;
  next: () => void;
  previous?: () => void;
  hasAfterCursor: boolean;
  hasBeforeCursor?: boolean;
  loading?: boolean;
  inverse?: boolean;
  onScrollToTop?: (force?: boolean) => void;
  onResetLista?: () => void;
  mensagemIdPesquisada?: string | null;
  disablePagination?: boolean;
  /** Quando true, scrolls manuais (false→true) não disparam onScrollToTop. Apenas a montagem inicial ainda dispara. */
  isMarkedAsUnread?: boolean;
};

const SCROLL_THRESHOLD = 10;
const PAGINATION_THRESHOLD = 200;
const MAX_AUTO_LOAD_ATTEMPTS = 5;

/**
 * Hook customizado que encapsula toda a lógica de scroll, paginação,
 * e controle de visibilidade para uma lista de mensagens virtualizada.
 */
export const useMessageScroll = ({
  rowVirtualizer,
  itemsLength,
  next,
  previous,
  hasAfterCursor,
  hasBeforeCursor,
  loading,
  inverse = true,
  mensagemIdPesquisada = null,
  onScrollToTop,
  onResetLista,
  disablePagination = false,
  isMarkedAsUnread = false,
}: UseMessageScrollProps) => {
  const [isTop, setIsTop] = useState(true);
  const parentRef = useRef<HTMLUListElement>(null);
  const previousItemLengthRef = useRef(0);
  const isInitialLoad = useRef(true);
  const loadingDirectionRef = useRef<'older' | 'newer' | null>(null);
  const autoLoadAttemptsRef = useRef(0);
  const previousHeightRef = useRef(0);
  const onScrollToTopRef = useRef(onScrollToTop);
  const isMarkedAsUnreadRef = useRef(isMarkedAsUnread);
  // Inicia como false para que a montagem inicial (isTop = true) seja tratada
  // como transição false → true, disparando a confirmação de leitura ao abrir
  // um atendimento que já estava marcado como não lido.
  const prevIsTopRef = useRef(false);
  // Controla se onScrollToTop já foi chamado ao menos uma vez neste ciclo de vida.
  // Permite distinguir a montagem inicial (sempre dispara) dos scrolls manuais
  // posteriores (não dispara se isMarkedAsUnread).
  const isFirstCallRef = useRef(true);

  const nextCallRef = useRef(next);
  const previousCallRef = useRef(previous);

  useEffect(() => {
    nextCallRef.current = next;
    previousCallRef.current = previous;
  }, [next, previous]);

  useEffect(() => {
    onScrollToTopRef.current = onScrollToTop;
  }, [onScrollToTop]);

  useEffect(() => {
    isMarkedAsUnreadRef.current = isMarkedAsUnread;
  }, [isMarkedAsUnread]);

  /**
   * Rola programaticamente para o início ou fim da lista.
   */
  const handleScroll = useCallback(() => {
    const list = parentRef.current;
    if (!list) return;

    if (mensagemIdPesquisada && hasBeforeCursor && onResetLista) {
      onResetLista();

      setTimeout(() => {
        const newList = parentRef.current;
        if (newList) {
          rowVirtualizer.scrollToOffset(inverse ? newList.scrollHeight : 0, {
            align: 'start',
            behavior: 'auto',
          });
        }
      }, 500);

      return;
    }

    rowVirtualizer.scrollToOffset(inverse ? list.scrollHeight : 0, {
      align: 'start',
      behavior: 'auto',
    });
  }, [
    inverse,
    rowVirtualizer,
    mensagemIdPesquisada,
    onResetLista,
    hasBeforeCursor,
  ]);

  /**
   * Escuta eventos globais para forçar o scroll para a base.
   */
  useEffect(() => {
    const checkSendMessageAtendenteNoScrollTop = (value: boolean) => {
      if (value) handleScroll();
    };
    Emitter.on(
      emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
      checkSendMessageAtendenteNoScrollTop,
    );
    return () => {
      Emitter.off(
        emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
        checkSendMessageAtendenteNoScrollTop,
      );
    };
  }, [handleScroll]);

  /**
   * Dispara o callback onScrollToTop apenas quando isTop transiciona de false → true.
   *
   * Regras:
   * - Montagem inicial (isFirstCallRef=true): sempre dispara, pois é o momento de
   *   selecionar o atendimento — mesmo que esteja marcado como não lido.
   * - Scrolls manuais posteriores: só dispara se o atendimento NÃO estiver marcado
   *   como não lido, evitando confirmar leitura ao subir/descer o scroll manualmente.
   *
   * Usar refs para o callback e para isMarkedAsUnread evita que recriações da função
   * ou mudanças desses valores acionem o efeito indevidamente.
   */
  useEffect(() => {
    const wasTop = prevIsTopRef.current;
    prevIsTopRef.current = isTop;

    if (!wasTop && isTop && onScrollToTopRef.current) {
      const isFirstCall = isFirstCallRef.current;
      isFirstCallRef.current = false;

      // Na montagem inicial, confirma leitura independentemente do status.
      // Em scrolls posteriores, só confirma se não estiver marcado como não lido.
      if (isFirstCall || !isMarkedAsUnreadRef.current) {
        onScrollToTopRef.current();
      }
    }
  }, [isTop]);

  /**
   * Se um bloco de mensagens carregadas não forem o suficiente para preencher a tela,
   * fazendo com que o scroll não apareça, carrega mais dados automaticamente.
   */
  useEffect(() => {
    const list = parentRef.current;
    if (!list || loading || disablePagination) return;

    let checkTimeoutId: number | null = null;

    const checkAndLoadMore = () => {
      if (loading) return;

      const { scrollHeight, clientHeight } = list;
      const hasScroll = scrollHeight > clientHeight;

      // Se não há scroll e ainda há dados para carregar
      if (
        !hasScroll &&
        hasAfterCursor &&
        autoLoadAttemptsRef.current < MAX_AUTO_LOAD_ATTEMPTS
      ) {
        autoLoadAttemptsRef.current += 1;
        loadingDirectionRef.current = 'older';
        previousItemLengthRef.current = itemsLength;
        nextCallRef.current();
      } else if (hasScroll) {
        // Reset o contador quando houver scroll suficiente
        autoLoadAttemptsRef.current = 0;
      }
    };

    // Verificação inicial após renderização
    checkTimeoutId = window.setTimeout(checkAndLoadMore, 150);

    // Monitorar mudanças de tamanho (quando mensagens são agrupadas/desagrupadas)
    const resizeObserver = new ResizeObserver(() => {
      if (checkTimeoutId) clearTimeout(checkTimeoutId);
      checkTimeoutId = window.setTimeout(checkAndLoadMore, 100);
    });

    resizeObserver.observe(list);

    return () => {
      if (checkTimeoutId) clearTimeout(checkTimeoutId);
      resizeObserver.disconnect();
    };
  }, [itemsLength, loading, hasAfterCursor, disablePagination]);

  /**
   * Gerencia eventos de scroll para detecção de topo e paginação manual.
   */
  useEffect(() => {
    const list = parentRef.current;
    if (!list) return;

    let rafId: number | null = null;

    const handleScrollEvent = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        const { scrollHeight, scrollTop, clientHeight } = list;

        // Atualizar isTop
        if (inverse) {
          setIsTop(scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD);
        } else {
          setIsTop(scrollTop <= SCROLL_THRESHOLD);
        }

        // Paginação manual (quando usuário faz scroll)
        if (!disablePagination && !loading) {
          if (scrollTop < PAGINATION_THRESHOLD && hasAfterCursor) {
            loadingDirectionRef.current = 'older';
            previousItemLengthRef.current = itemsLength;
            nextCallRef.current();
          } else if (
            scrollHeight - scrollTop - clientHeight < PAGINATION_THRESHOLD &&
            hasBeforeCursor &&
            mensagemIdPesquisada
          ) {
            loadingDirectionRef.current = 'newer';
            previousItemLengthRef.current = itemsLength;
            previousCallRef.current?.();
          }
        }

        rafId = null;
      });
    };

    list.addEventListener('scroll', handleScrollEvent, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      list.removeEventListener('scroll', handleScrollEvent);
    };
  }, [
    inverse,
    hasAfterCursor,
    hasBeforeCursor,
    loading,
    itemsLength,
    disablePagination,
    mensagemIdPesquisada,
  ]);

  /**
   * Previne scroll quando paginação está desabilitada.
   */
  useEffect(() => {
    const listElement = parentRef.current;
    if (!listElement || !disablePagination) return;

    const preventScroll = (e: Event) => e.preventDefault();
    const options = { passive: false };

    listElement.addEventListener('wheel', preventScroll, options);
    listElement.addEventListener('touchmove', preventScroll, options);

    return () => {
      listElement.removeEventListener('wheel', preventScroll);
      listElement.removeEventListener('touchmove', preventScroll);
    };
  }, [disablePagination]);

  /**
   * Gerencia o scroll programático em resposta a mudanças nos dados (itemsLength).
   * Lida com o carregamento inicial, novas mensagens e rolagem pós-paginação.
   */
  useEffect(() => {
    const currentItemLength = itemsLength;
    const previousItemLength = previousItemLengthRef.current;

    if (isInitialLoad.current && currentItemLength > 0) {
      isInitialLoad.current = false;

      // aguarda o layout estabilizar antes de rolar
      requestAnimationFrame(() => {
        rowVirtualizer.scrollToIndex(currentItemLength - 1, {
          align: 'end',
          behavior: 'auto',
        });

        // força o ajuste final no próximo frame
        requestAnimationFrame(() => {
          const list = parentRef.current;
          if (list) {
            rowVirtualizer.scrollToOffset(list.scrollHeight, {
              align: 'start',
              behavior: 'auto',
            });
          }
        });
      });
    } else if (currentItemLength > previousItemLength) {
      const diff = currentItemLength - previousItemLength;

      // Lógica condicional baseada na direção do carregamento
      if (loadingDirectionRef.current === 'older') {
        // Mantém a posição do scroll relativa ao topo antigo
        rowVirtualizer.scrollToIndex(diff, {
          align: 'start',
          behavior: 'auto',
        });
      } else if (loadingDirectionRef.current === 'newer') {
        rowVirtualizer.scrollToIndex(previousItemLength - 1, {
          align: 'end',
          behavior: 'auto',
        });
      } else if (diff > 0 && isTop) {
        // Nova mensagem recebida em tempo real enquanto o usuário está na base
        requestAnimationFrame(() => {
          const list = parentRef.current;
          if (list) {
            rowVirtualizer.scrollToOffset(list.scrollHeight, {
              align: 'start',
              behavior: 'auto',
            });
          }
        });
        if (onScrollToTopRef.current) {
          onScrollToTopRef.current(true);
        }
      }

      loadingDirectionRef.current = null;
    }
    previousItemLengthRef.current = itemsLength;
  }, [itemsLength, rowVirtualizer, isTop]);

  useLayoutEffect(() => {
    const list = parentRef.current;
    if (!list) return;

    const currentHeight = list.scrollHeight;
    const previousHeight = previousHeightRef.current;

    if (loadingDirectionRef.current === 'older' && previousHeight > 0) {
      const heightDiff = currentHeight - previousHeight;
      const scrollAdjustment = list.scrollTop + heightDiff;

      list.scrollTop = scrollAdjustment;
    }

    previousHeightRef.current = currentHeight;
  }, [itemsLength]);

  return { parentRef, isTop, handleScroll };
};
